import React, { useState } from 'react';
import { toast } from 'sonner';
import { MapPin, CheckCircle2, KeyRound, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { verifyDrop } from '../services/api';

// Per-drop 6-digit verification for multi-drop deliveries.
// Each drop has its own code; the driver collects it from the receiver on
// arrival. The final dropoff (with proof photo) is blocked by the backend
// until every intermediate drop here is verified.
export default function VerifyDropsPanel({ delivery, onChange }) {
  const drops = Array.isArray(delivery?.drops) ? delivery.drops : [];
  const [codes, setCodes] = useState({});
  const [submitting, setSubmitting] = useState({});

  if (!drops.length) return null;

  const handleVerify = async (idx) => {
    const code = (codes[idx] || '').trim();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      toast.error('Enter the 6-digit code given by the receiver.');
      return;
    }
    setSubmitting((s) => ({ ...s, [idx]: true }));
    try {
      await verifyDrop(delivery.id, idx, code);
      toast.success(`Drop #${idx + 1} verified.`);
      setCodes((c) => ({ ...c, [idx]: '' }));
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not verify drop.');
    } finally {
      setSubmitting((s) => ({ ...s, [idx]: false }));
    }
  };

  const remaining = drops.filter((d) => !d.verified_at).length;

  return (
    <div className="mt-3 p-4 rounded-md border border-amber-200 bg-amber-50/60" data-testid={`verify-drops-panel-${delivery.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-amber-700" />
          <h4 className="font-bold text-sm text-amber-900">Multi-drop verification ({drops.length} stop{drops.length === 1 ? '' : 's'})</h4>
        </div>
        <span
          className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
            remaining === 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
          }`}
          data-testid={`drops-remaining-${delivery.id}`}
        >
          {remaining === 0 ? 'All verified' : `${remaining} pending`}
        </span>
      </div>
      <p className="text-[11px] text-amber-900/80 mb-3">
        Ask each receiver for their 6-digit code on arrival. The final delivery can only be completed once every stop here is verified.
      </p>
      <div className="space-y-2">
        {drops.map((drop, idx) => {
          const verified = !!drop.verified_at;
          return (
            <div
              key={idx}
              className={`flex flex-col sm:flex-row sm:items-center gap-2 p-2.5 rounded border ${
                verified ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'
              }`}
              data-testid={`verify-drop-row-${idx}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                  <MapPin className="w-3 h-3 text-orange-600" />
                  Drop #{idx + 1}
                </div>
                <p className="text-sm text-gray-900 truncate">{drop.address}</p>
                {drop.receiver_name && (
                  <p className="text-[11px] text-gray-500 truncate">For {drop.receiver_name}</p>
                )}
              </div>
              {verified ? (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 whitespace-nowrap" data-testid={`drop-verified-${idx}`}>
                  <CheckCircle2 className="w-4 h-4" /> Verified
                </span>
              ) : (
                <div className="flex items-stretch gap-2 sm:w-auto">
                  <Input
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="6-digit"
                    value={codes[idx] || ''}
                    onChange={(e) =>
                      setCodes((c) => ({
                        ...c,
                        [idx]: e.target.value.replace(/\D/g, '').slice(0, 6),
                      }))
                    }
                    className="w-28 text-center font-mono tracking-widest"
                    data-testid={`drop-code-input-${idx}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => handleVerify(idx)}
                    disabled={!!submitting[idx]}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid={`drop-verify-btn-${idx}`}
                  >
                    {submitting[idx] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Verify'}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
